import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client.ts";
import { useAuthStore } from "../store/useAuthStore.ts";
import { NoReviewNotice } from "../components/NoReviewNotice.tsx";
import {
  FileSearch,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Unlink,
  Bot,
  FileText,
  XCircle,
  ChevronRight,
} from "lucide-react";

interface ReviewPipelineStat {
  review: { id: string; name: string; type: string };
  assigned: number;
  awaitingAi: number;
  awaitingScreening: number;
  fullText: number;
  excluded: number;
  included: number;
  extracted: number;
  completed: number;
}

interface ReviewStatsResponse {
  stats: ReviewPipelineStat[];
}

interface StudyStats {
  total: number;
  unallocated: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isCoordinator = user?.role === "COORDINATOR" || user?.role === "ADMIN";

  const { data: reviewStats, isLoading: loadingReview } = useQuery({
    queryKey: ["review-stats"],
    queryFn: () => api.get<ReviewStatsResponse>("/studies/review-stats"),
  });

  const { data: studyStats, isLoading: loadingStudy } = useQuery({
    queryKey: ["study-stats"],
    queryFn: () => api.get<StudyStats>("/studies/stats"),
  });

  const loading = loadingReview || loadingStudy;
  // Counts are scoped server-side to the user's own reviews; with none there
  // is nothing meaningful to show, only zeros.
  const noReviews = !isCoordinator && !!reviewStats && reviewStats.stats.length === 0;

  const newCount = studyStats?.byStatus["NEW"] ?? 0;
  const unallocated = studyStats?.unallocated ?? 0;
  const awaitingScreening = Math.max(0, newCount - unallocated);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">
          neuroXRehab WG1: XR-based spatial neglect systematic reviews
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      )}

      {noReviews && <NoReviewNotice />}

      {studyStats && !noReviews && (
        <>
          <div className={`grid grid-cols-2 md:grid-cols-3 ${isCoordinator ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4 mb-8`}>
            <StatCard
              icon={<FileSearch className="h-5 w-5 text-primary" />}
              label="Total records"
              value={studyStats.total}
            />
            {/* Unallocated papers belong to no review yet: coordinator triage only */}
            {isCoordinator && (
              <StatCard
                icon={<Unlink className="h-5 w-5 text-muted-foreground" />}
                label="Unallocated"
                value={unallocated}
                dimmed={unallocated === 0}
                tooltip="Not yet assigned to any review. AI may have excluded these from all reviews, or processing is still running. Click to review."
                onClick={unallocated > 0 ? () => navigate("/screening?mode=unallocated") : undefined}
              />
            )}
            <StatCard
              icon={<ClipboardList className="h-5 w-5 text-amber-500" />}
              label="Awaiting screening"
              value={awaitingScreening}
              tooltip="Assigned to at least one review and ready for human screening."
              onClick={awaitingScreening > 0 ? () => navigate("/screening") : undefined}
            />
            <StatCard
              icon={<CheckCircle2 className="h-5 w-5 text-green-500" />}
              label="Included"
              value={
                (studyStats.byStatus["INCLUDED"] ?? 0) +
                (studyStats.byStatus["EXTRACTED"] ?? 0)
              }
            />
            <StatCard
              icon={<AlertTriangle className="h-5 w-5 text-destructive" />}
              label="Excluded"
              value={studyStats.byStatus["EXCLUDED"] ?? 0}
            />
          </div>

          {Object.keys(studyStats.bySource).length > 0 && (
            <div className="mb-8 p-4 border rounded-lg bg-card">
              <h2 className="text-sm font-semibold mb-3">Records by source</h2>
              <div className="flex flex-wrap gap-2">
                {Object.entries(studyStats.bySource)
                  .sort(([, a], [, b]) => b - a)
                  .map(([src, count]) => (
                    <span
                      key={src}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-secondary text-secondary-foreground"
                    >
                      <span className="font-medium">{SOURCE_LABELS[src] ?? src}</span>
                      <span className="opacity-70">{count}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {reviewStats && !noReviews && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Reviews</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {reviewStats.stats.map((s) => (
              <button
                key={s.review.id}
                onClick={() => navigate(`/screening?reviewId=${s.review.id}`)}
                className="border rounded-lg p-5 bg-card text-left hover:border-primary/50 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
                      {s.review.name}
                    </h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground">
                      {s.review.type}
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                </div>

                <dl className="space-y-1.5 text-sm">
                  <Row label="Assigned" value={s.assigned} bold />

                  {s.awaitingAi > 0 && (
                    <Row
                      label={<span className="flex items-center gap-1"><Bot className="h-3 w-3" />Awaiting AI</span>}
                      value={s.awaitingAi}
                      dimmed
                    />
                  )}

                  {s.awaitingScreening > 0 && (
                    <Row
                      label={<span className="flex items-center gap-1"><ClipboardList className="h-3 w-3" />Awaiting screening</span>}
                      value={s.awaitingScreening}
                      highlight
                    />
                  )}

                  {s.fullText > 0 && (
                    <Row
                      label={<span className="flex items-center gap-1"><FileText className="h-3 w-3" />Full-text review</span>}
                      value={s.fullText}
                    />
                  )}

                  <div className="border-t pt-1.5 mt-1.5 space-y-1.5">
                    {s.excluded > 0 && (
                      <Row
                        label={<span className="flex items-center gap-1"><XCircle className="h-3 w-3" />Excluded</span>}
                        value={s.excluded}
                        muted
                      />
                    )}
                    <Row
                      label={<span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Included</span>}
                      value={s.included}
                    />
                    <Row label="Data extracted" value={s.extracted} bold={s.extracted > 0} />
                    <Row label="Completed" value={s.completed} bold={s.completed > 0} />
                  </div>
                </dl>

                <p className="text-xs text-primary mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open screening queue →
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  dimmed,
  tooltip,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  dimmed?: boolean;
  tooltip?: string;
  onClick?: () => void;
}) {
  const base = `border rounded-lg p-4 bg-card transition-all ${dimmed ? "opacity-50" : ""}`;
  const interactive = onClick
    ? "cursor-pointer hover:border-primary/50 hover:shadow-sm group"
    : "";

  return (
    <div className={`${base} ${interactive}`} title={tooltip} onClick={onClick}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        {onClick && (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-primary ml-auto transition-colors" />
        )}
      </div>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
      <p className={`text-xs mt-0.5 ${onClick ? "text-muted-foreground group-hover:text-primary transition-colors" : "text-muted-foreground"}`}>
        {label}
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  dimmed,
  muted,
  highlight,
}: {
  label: React.ReactNode;
  value: number;
  bold?: boolean;
  dimmed?: boolean;
  muted?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className={`flex justify-between ${dimmed ? "text-muted-foreground/60" : muted ? "text-muted-foreground" : highlight ? "text-amber-600" : "text-muted-foreground"}`}>
      <dt>{label}</dt>
      <dd className={bold ? "font-bold text-foreground" : "font-medium"}>{value}</dd>
    </div>
  );
}

const SOURCE_LABELS: Record<string, string> = {
  PUBMED: "PubMed",
  EMBASE: "Embase",
  CINAHL: "CINAHL",
  SCOPUS: "Scopus",
  WEB_OF_SCIENCE: "Web of Science",
  IEEE: "IEEE Xplore",
  COCHRANE: "Cochrane Library",
  MANUAL: "Manual",
  OTHER: "Other",
};
